import React, { useState, useContext, useEffect } from 'react';
import RiskOverviewSectionHeader from '../RiskOverviewSectionHeader/RiskOverviewSectionHeader';
import CTAButton from '/src/Components/CTAButton/CTAButton';
import UserPersonalInfoForm from '/src/Components/UserPersonalInfoForm/UserPersonalInfoForm';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import GaugeChart from 'react-gauge-chart';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import { CreateGoogleMapsStreetviewUrl } from '/src/services/CreateGoogleMapsStreetviewUrl';
import { WindowSizeContext, TestStatusContext, AddressSearchContext } from '/src/App';
import { usePropertyGaLogging } from '/src/contexts/PropertyGaLoggingContext';


import './style.css';
import { AreaDataContext } from '/src/Views/PropertyExplorer/PropertyExplorer';

const PuttingItAllTogether = ({ propertyData, title }) => {

    const { deviceType } = useContext(WindowSizeContext);
    // const { LOG_GA_EVENT } = useContext(AddressSearchContext);
    const { handleLoggingGaEventFromProperty } = usePropertyGaLogging();
    const { userPersonalInfo, quollScoreAnalysis, goToYourOptions } = useContext(AreaDataContext);

    const propertyDetails = propertyData?.data?.property_details;
    const latitude = propertyDetails?.lat;
    const longitude = propertyDetails?.long;
    const streetviewImageUrl = CreateGoogleMapsStreetviewUrl(latitude, longitude);

    const [showQuollScore, setShowQuollScore] = useState(false);
    const [quollScoreContent, setQuollScoreContent] = useState(null);
    const [showUserPreferencesPopup, setShowUserPreferencesPopup] = useState(false);


    // useEffect to check for userPersonalInfo and quollScoreAnalysis
    useEffect(() => {
        if (userPersonalInfo && quollScoreAnalysis) {
            const userPersonalInfoIsComplete = userPersonalInfo?.isComplete || false;
            if (userPersonalInfoIsComplete) {
                // Set to true assuming it not already true
                if (!showQuollScore) {
                    setShowQuollScore(true);
                }
            } else {
                // Set to false assuming it not already false
                if (showQuollScore) {
                    setShowQuollScore(false);
                }
            }
        }
    }, [userPersonalInfo, quollScoreAnalysis]);

    // Create the Quoll score content assuming quollScoreAnalysis is complete and showQuollScore is true
    useEffect(() => {
        if (quollScoreAnalysis && showQuollScore && userPersonalInfo) {
            const { budget, ltv, opex, quollScore } = quollScoreAnalysis;
            const { availableFunds, expectedPurchasePrice, householdIncome, isComplete, monthlyMortgagePayment, relationToProperty } = userPersonalInfo

            const budgetScore = quollScore?.budgetScore || 0;
            const opexScore = quollScore?.opexScore || 0;
            const ltvScore = quollScore?.ltvCombinedScore || 0;
            const quollScoreNumber = quollScore?.quollScore || 0;
            const quollScoreLetter = quollScore?.quollScoreLetter || '';

            const scoresArray = [
                {
                    id: 'budget',
                    score: budgetScore,
                },
                {
                    id: 'opex',
                    score: opexScore,
                },
                {
                    id: 'ltv',
                    score: ltvScore,
                }
            ];

            // Sort scores from highest to lowest
            const sortedScores = [...scoresArray].sort((a, b) => b.score - a.score);

            // Add ranks with ties
            let currentRank = 1;
            let currentScore = sortedScores[0].score;
            let increment = 0;

            const rankedScores = sortedScores.map((item, index) => {
                if (item.score < currentScore) {
                    currentRank += increment;
                    currentScore = item.score;
                    increment = 1;
                } else {
                    increment++;
                }
                return { ...item, rank: currentRank };
            });

            console.log('rankedScores', rankedScores);


            // Challenge with... sentences
            const challengeWithBudgetSentence = `The challenge with unforessen, sudden costs is in the name...they're unexpected.`;
            const challengeWithOpexSentence = `The challenge with rising ownership expenses is that they're kind of like a pot of boiling water, and the owner is the frog. .`;
            const challengeWithLtvSentence = `The challenge with market value is that it's uncertain.`;

            // Opportunity with... sentences
            const opportunityWithBudgetSentence = `The opportunity with unforeseen, sudden costs is that they're unexpected.`;


            let quollScoreOverallWorrySentence1 = '';
            let quollScoreOverallWorrySentence2 = '';
            console.log('relationToProperty', relationToProperty);

            if (quollScoreLetter === 'A') {
                quollScoreOverallWorrySentence1 = `Overall, you do not need to worry about the impact of climate risk on this home.`;
            } else if (quollScoreLetter === 'B') {
                quollScoreOverallWorrySentence1 = `Overall, you do not need to worry too much about the impact of climate risk on this home.`;
            } else if (quollScoreLetter === 'C') {
                quollScoreOverallWorrySentence1 = `You should be at least somewhat concerned about the impact of climate risk on this home,`;
                if (relationToProperty === 'owner') {
                    quollScoreOverallWorrySentence2 = ` actively monitoring this, and starting to plan around taking action to lower the potential financial impact.`;
                } else if (relationToProperty === 'buyer') {
                    quollScoreOverallWorrySentence2 = ` and accounting for it as you think through the purchase decision.`;
                }
            } else if ((quollScoreLetter === 'D') || (quollScoreLetter === 'F')) {
                quollScoreOverallWorrySentence1 = `You should be concerned about the impact of climate risk on this home,`;
                if (relationToProperty === 'owner') {
                    quollScoreOverallWorrySentence2 = ` and actively taking steps to lower the potential financial impact.`;
                } else if (relationToProperty === 'buyer') {
                    quollScoreOverallWorrySentence2 = ` and truly account for it if you are seriously considering purchasing this home.`;
                }
            }

            const quollScoreOverallWorrySentence = `${quollScoreOverallWorrySentence1} ${quollScoreOverallWorrySentence2}`;



            // Sentences for relative risk of the different financial components

            // Filter the rankedScores array to get all scores with rank 1
            const highestScores = rankedScores.filter(score => score.rank === 1);
            const highestScoreNumber = highestScores[0].score;
            let highestScoreNumberNuance = '';
            if (highestScoreNumber < 5) {
                highestScoreNumberNuance = ", though the worry level is still relatively low."
            } else {
                highestScoreNumberNuance = "."
            }

            let highestScoreMultiplesOrSingleNuance = 'The area of greatest concern is ';
            if (highestScores.length > 1) {
                highestScoreMultiplesOrSingleNuance = 'The areas of greatest concern are ';
            }

            let highestScoreTypeNames = '';
            // Map through the highestScores array add the type name to the highestScoreTypeNames string based on the id
            highestScores.forEach((score, index) => {
                if (index === 0) {
                    highestScoreTypeNames += 'the ';
                } else if (index === highestScores.length - 1) {
                    highestScoreTypeNames += ' and ';
                }
                if (score.id === 'budget') {
                    highestScoreTypeNames += 'potential unforeseen costs';
                } else if (score.id === 'opex') {
                    highestScoreTypeNames += 'growth in recurring expenses';
                } else if (score.id === 'ltv') {
                    highestScoreTypeNames += 'impact on market value';
                }
                if ((index < highestScores.length - 1) && (highestScores.length !== 2)) {
                    highestScoreTypeNames += ', ';
                }
            });

            let importantToNoteSentence = '';
            const isSuddenCostsHighest = highestScores.some(score => score.id === 'budget');
            const isOpexExpenseHighest = highestScores.some(score => score.id === 'opex');
            const isLtvExpenseHighest = highestScores.some(score => score.id === 'ltv');
            if (isLtvExpenseHighest) {
                importantToNoteSentence = 'What makes staying on top of market risk so challenging is the signals are constantly evolving and tipping points can come on quickly. The dynamic nature of this means you need to be constantly monitoring these trends. ';
            } else if (isOpexExpenseHighest) {
                importantToNoteSentence = "What makes recurring expenses so challenging is it leaves you like the frog in a pot of boiling water. By the time you fully realize the problem, you might not be able to afford to stay in this home. ";
            } else if (isSuddenCostsHighest) {
                importantToNoteSentence = 'What makes sudden costs so challenging is they are unpredictable, can come from multiple directions, and compound quickly. Keeping enough money easily accessible is important, but can be challenging when life dictates needing to spend those funds elsewhere. ';
            }

            const highestScoreTypeNamesSentence = `${highestScoreMultiplesOrSingleNuance} ${highestScoreTypeNames}${highestScoreNumberNuance} ${importantToNoteSentence}`;

            const combinedWorryText = `${quollScoreOverallWorrySentence} ${highestScoreTypeNamesSentence}`;


            // Boolean check on whether the highestScores includes the recurring expense
            const isRecurringExpenseHighest = highestScores.some(score => score.id === 'opex');
            console.log('isRecurringExpenseHighest', isRecurringExpenseHighest);
            let recurringExpenseWorrySentence = "You may yet not be feeling the everyday financial impact climate is having on this home or what is means for the home's future. ";
            if (isRecurringExpenseHighest) {
                recurringExpenseWorrySentence = 'It is likely that you are already starting to feel the financial impact of climate on this home, though you may not have realized why. ';
            }

            const recurringExpensesSecondText = "The best way to avoid climate hurting your wallet is through home improvements to make the propety more resilient. We've created a tailored list of a recommendations for this home, with everything from guides to payoff projections. Head to the recommendations section to learn more."

            const combinedRecurringExpenseWorryText = `${recurringExpenseWorrySentence} ${recurringExpensesSecondText}`;

            setQuollScoreContent({
                worrySentence: quollScoreOverallWorrySentence,
                highestScoreTypeNamesSentence: highestScoreTypeNamesSentence,
                combinedWorryText: combinedWorryText,
                combinedRecurringExpenseWorryText: combinedRecurringExpenseWorryText,
            });
        } else {
            if (quollScoreContent) {
                setQuollScoreContent(null);
            }
        }
    }, [quollScoreAnalysis, showQuollScore, userPersonalInfo]);


    const handleShowUserPreferencesPopup = () => {
        setShowUserPreferencesPopup(!showUserPreferencesPopup);

        if (showUserPreferencesPopup) {
            handleLoggingGaEventFromProperty('customize_button_putting_it_all_together')
        }
    }


    // handler to get the quoll score letter color
    const getQuollScoreLetterColor = (letter) => {
        let color = 'green';
        switch (letter) {
            case 'A':
                color = 'green';
                break;
            case 'B':
            case 'C':
                color = 'yellow';
                break;
            case 'D':
            case 'F':
                color = 'red';
                break;
            default:
                color = 'green';
                break;
        }
        return color;
    }
    const quollScoreComponentColors = [
        "#34C759",
        "#66C237",
        "#93BD15",
        "#edf051",
        "#ffe570",
        "#FFD60A",
        "#FFB007",
        "#FF8A04",
        "#FF5C31",
        "#FF3B30"
    ]


    const getQuollScoreComponentColor = (component) => {
        return quollScoreComponentColors[component - 1];
    }

    const quollScoreBreakdown = quollScoreAnalysis?.quollScore || {};
    const quollScoreLetter = quollScoreBreakdown?.quollScoreLetter || '';
    const budgetScore = quollScoreBreakdown?.budgetScore || 0;
    const opexScore = quollScoreBreakdown?.opexScore || 0;
    const ltvScore = quollScoreBreakdown?.ltvCombinedScore || 0;

    const impactAcrossTimeOptions = [
        {
            id: 1,
            icon: 'broken-home-green.png',
            timeframe: 'Sudden',
            title: 'Unforeseen Fixes',
            description: "Costs you may not have budgeted for.",
            score: budgetScore
        },
        {
            id: 2,
            icon: 'home_bills.png',
            timeframe: 'Recurring',
            title: 'General Expenses',
            description: 'Think insurance, maintenance, taxes, utilities etc.',
            score: opexScore
        },
        {
            id: 3,
            icon: 'home-ltv-icon.png',
            timeframe: 'Long-Term',
            title: 'Market Value',
            description: 'What happens when you go to sell the property?',
            score: ltvScore
        }
    ]

    const generateScoreText = "How Worried Should I Be?"
    const personalInfoNotCompleteIntroSentence = "We've walked you through different ways in which this home's climate risk profile turns into physical impact, but assessing whether or not that makes the home 'risky' comes down to you and <i>your</i> financial profile and goals. Click below to see exactly what this analysis means for you!"
    const personalInfoNotCompleteConclusionSentence = "Whether or not you personalize the analysis, odds are you are interested in anything that will lower your risk and lower the financial impact. That's where home resilience comes in. Check out our recommendations for this home to see which projects make sense for this home and what the payback for each is."

    return (
        <div className="tab-content-container">
            <RiskOverviewSectionHeader title={title} />
            {!showQuollScore && (
                <>
                    <p className="putting-it-all-together-intro-sentence text-align-center" dangerouslySetInnerHTML={{ __html: personalInfoNotCompleteIntroSentence }} />
                    <CTAButton onClick={handleShowUserPreferencesPopup} text={generateScoreText} />
                    {showUserPreferencesPopup && (
                        <UserPersonalInfoForm handleOutsideClick={handleShowUserPreferencesPopup} />
                    )}
                    <p className="putting-it-all-together-intro-sentence text-align-center" dangerouslySetInnerHTML={{ __html: personalInfoNotCompleteConclusionSentence }} />
                </>
            )}
            {showQuollScore && (
                <div className="putting-it-all-together-quoll-score-details-container">
                    {quollScoreContent && (
                        <p className="putting-it-all-together-quoll-score-worry-sentence text-align-center" dangerouslySetInnerHTML={{ __html: quollScoreContent.worrySentence }} />
                    )}

                    <div className={`putting-it-all-together-scores-frame ${deviceType}`}>
                        <div className="putting-it-all-together-home-image-and-quoll-score-container">
                            {streetviewImageUrl && (
                                <img
                                    src={streetviewImageUrl}
                                    alt="Street View"
                                // className="individual-property-streetview-image"
                                />
                            )}
                            <div className="putting-it-all-together-home-image-and-quoll-score-container-overlay">
                                <div className={`putting-it-all-together-quoll-score-letter-container ${getQuollScoreLetterColor(quollScoreLetter)}`}>
                                    <span> {quollScoreLetter} </span>
                                </div>
                            </div>
                        </div>

                        <CurlyBraceDivider direction={deviceType === 'desktop' ? 'right' : 'down'} color='grey' />
                        <div className={`putting-it-all-together-quoll-score-breakdown-container ${deviceType}`}>
                            <div className={`putting-it-all-together-impact-across-time-options-container ${deviceType}`}>
                                {impactAcrossTimeOptions.map((option, index) => (
                                    <div key={index} className="putting-it-all-together-impact-across-time-option">
                                        <div className="putting-it-all-together-impact-across-time-option-gauge-container">
                                            <GaugeChart
                                                id={`putting-it-all-together-impact-across-time-option-gauge`}
                                                nrOfLevels={10}
                                                colors={quollScoreComponentColors}
                                                arcWidth={0.25}
                                                percent={option.score / 10}
                                                textColor="transparent"
                                                needleScale={.65}
                                                needleColor={getQuollScoreComponentColor(option.score)}
                                                needleBaseColor="#666666"
                                                className="putting-it-all-together-impact-across-time-option-gauge"
                                                hideText={true}
                                                style={{
                                                    height: '100%',
                                                }}
                                            />
                                        </div>
                                        <div
                                            className={`putting-it-all-together-impact-across-time-option-icon-wrapper`}
                                        // style={{ backgroundColor: getQuollScoreComponentColor(option.score) }}
                                        >
                                            <img src={CreateCloudfrontUrl(option.icon)} alt={option.title} />
                                        </div>
                                        <div className="putting-it-all-together-impact-across-time-option-text-wrapper">
                                            <div
                                                className="putting-it-all-together-impact-across-time-option-timeframe-container"
                                            // style={{ backgroundColor: getQuollScoreComponentColor(option.score) }}
                                            >
                                                <span>{option.timeframe}</span>
                                            </div>
                                            <span className="putting-it-all-together-impact-across-time-option-title">{option.title}</span>
                                            {/* <span className="putting-it-all-together-impact-across-time-option-description text-align-center">{option.description}</span> */}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                    {quollScoreContent && (
                        <>
                            <div className="putting-it-all-together-quoll-score-worry-sentences-container">
                                <p className="putting-it-all-together-quoll-score-worry-sentence text-align-center" dangerouslySetInnerHTML={{ __html: quollScoreContent.highestScoreTypeNamesSentence }} />
                                <p className="putting-it-all-together-quoll-score-worry-sentence text-align-center" dangerouslySetInnerHTML={{ __html: quollScoreContent.combinedRecurringExpenseWorryText }} />
                            </div>
                            <CTAButton onClick={goToYourOptions} text={"See Resilience Recommendations"} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PuttingItAllTogether;
